import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Search as InspectIcon } from 'lucide-react';

import { useTranslation } from 'react-i18next';

import Loading from '../utils/Loading';
import SurveyData from '../utils/SurveyData';
import LocationInfoCard from '../payloadNew/LocationInfoCard';
import PayloadScoreCard from '../payloadNew/PayloadScoreCard';
import PayloadInsightsCard, { deriveInsightCounts } from '../payloadNew/PayloadInsightsCard';
import SurveyContent from './SurveyContent';
import { Icons } from '../payloadNew/Icons';
import { Tour, useTour } from '../tour';
import getSurveyResponseSteps from '../tour/steps/surveyResponseSteps';

import '../payloadNew/payloadNew.css';

import {
	get_survey_response,
	get_survey_schema_display,
	set_survey_images
} from '../../actions/surveyActions';
import { get_locations } from '../../actions/locationActions';
import { get_indicators_results } from '../../actions/indicatorsActions';

import parseSurveyData, { resolveSchemaId } from '../../utils/parseSurveyData';
import isEmpty from '../../utils/isEmpty';

// Rail entries — only the sections that have been migrated to the
// PayloadNew design system so far. Add more as each section gets ported.
const SECTIONS = [
	{ id: 'pn-identity', labelKey: 'payload.rail.store',    icon: 'store' },
	{ id: 'pn-payload',  labelKey: 'payload.rail.details',  icon: 'phone' },
	{ id: 'pn-score',    labelKey: 'payload.rail.score',    icon: 'award' },
	{ id: 'pn-insights', labelKey: 'payload.rail.insights', icon: 'bulb'  },
	{ id: 'pn-content',  labelKey: 'payload.rail.content',  icon: 'grid'  },
];

const SurveyResponse = () => {
	const { t } = useTranslation();

	const { response_id } = useParams();
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const inspectionHref = `/surveys/responses/${response_id}/inspection`;

	const tour = useTour({ storageKey: 'survey-response-tour-v1', autoStart: true });
	const tourSteps = useMemo(() => getSurveyResponseSteps(t), [t]);

	const [urlCopied, setUrlCopied] = useState(false);

	// Copy the current URL to the clipboard. Tries the async Clipboard API
	// first (only works on secure contexts) and falls back to a hidden
	// textarea + document.execCommand('copy') for older or insecure setups.
	// On success the "Copied!" label flashes for 1.5s before reverting.
	const handleCopyUrl = async () => {
		// Share a clean, canonical link to the survey — drop the query params
		// (`?token=...`, search state, etc.) and the rail-anchor hash
		// (`#pn-score`, `#pn-identity`, ...) so the recipient lands on the
		// page itself and not on whichever section the sharer was scrolled to.
		const url = window.location.origin + window.location.pathname;
		let ok = false;
		if (navigator.clipboard && window.isSecureContext) {
			try { await navigator.clipboard.writeText(url); ok = true; }
			catch (e) { ok = false; }
		}
		if (!ok) {
			const ta = document.createElement('textarea');
			ta.value = url;
			ta.setAttribute('readonly', '');
			ta.style.position = 'fixed';
			ta.style.opacity = '0';
			ta.style.left = '-9999px';
			document.body.appendChild(ta);
			ta.focus();
			ta.select();
			try { ok = document.execCommand('copy'); } catch (e) { ok = false; }
			document.body.removeChild(ta);
		}
		if (ok) {
			setUrlCopied(true);
			setTimeout(() => setUrlCopied(false), 1500);
		}
	};

	const { isAdminUser } = useSelector(state => state.auth);

	const {
		survey_loading,
		survey_response,
		survey_schema_display,
		survey_images
	} = useSelector(state => state.survey);

	const { locations } = useSelector(state => state.location);

	const { app_config } = useSelector(state => state.organization);

	const { indicators_results, indicators_errors } = useSelector(state => state.indicators);

	const [locationInfo, setLocationInfo] = useState(null);
	const [parsedFields, setParsedFields] = useState([]);

	// `/api/indicators/results/overview` flattens the score record (the array
	// of groups sits at `indicators`, and `score`/`date` live at the root). The
	// shared <PayloadScoreCard /> expects PayloadNew's nested shape, so we
	// remap here instead of duplicating logic inside the card.
	const scoreIndicators = useMemo(() => {
		if (!indicators_results) return null;
		return {
			details: Array.isArray(indicators_results.indicators) ? indicators_results.indicators : [],
			score: indicators_results.score,
			_id: indicators_results.reference,
			date: indicators_results.date,
		};
	}, [indicators_results]);

	// The results/overview response already carries the evaluated rules for
	// survey responses under `evaluation.results` (same row shape as the
	// payload flow), so we derive the insight counts from there instead of
	// firing a second request.
	const insightCounts = useMemo(
		() => deriveInsightCounts(indicators_results?.evaluation?.results),
		[indicators_results]
	);
	const hasInsights = insightCounts.total > 0 && !indicators_errors?.indicators_results;
	const insightsHref = `/surveys/responses/${response_id}/insights`;

	// Resolve the schema's display label: prefer the alias the org's app-config
	// defines for it (`data.surveysSchemas.alias[<schema name>]`), otherwise
	// fall back to the raw schema name.
	const schemaLabel = useMemo(() => {
		const rawName = survey_response?.survey_schema?.name;
		if (!rawName || !Array.isArray(app_config)) return rawName;
		const orgId = survey_response?.organization?.$oid ?? survey_response?.fields?.organization;
		if (!orgId) return rawName;
		const orgCfg = app_config.find((c) => c?.organization?.$oid === orgId);
		const alias = orgCfg?.data?.surveysSchemas?.alias?.[rawName];
		return alias || rawName;
	}, [survey_response, app_config]);

	useEffect(() => {
		if (response_id) {
			dispatch(get_survey_response(response_id));
			dispatch(get_indicators_results(response_id));
		}
	}, [response_id]);

	useEffect(() => {
		if (!isEmpty(survey_response)) {
			const schema_id = resolveSchemaId(survey_response);
			if (schema_id) {
				dispatch(get_survey_schema_display(schema_id));
			}

			const orgId = survey_response?.organization?.$oid ?? survey_response?.fields?.organization;
			const code = survey_response?.fields?.code;
			if (orgId && code) {
				dispatch(get_locations({ organization: orgId, code }));
			}
		}
	}, [survey_response]);

	useEffect(() => {
		if (locations?.count > 0) {
			setLocationInfo(locations.locations[0]);
		}
	}, [locations]);

	useEffect(() => {
		if (survey_schema_display && survey_response?.fields) {
			const parsed = parseSurveyData(survey_schema_display, survey_response.fields, survey_response.photos);
			setParsedFields(parsed);
			dispatch(set_survey_images(survey_response, survey_schema_display));
		}
	}, [survey_schema_display, survey_response]);

	if (survey_loading) {
		return <Loading msg={t('payload.loading')} />;
	}

	return (
		<div id={localStorage.getItem('theme')}>
			<div className='payload-new'>
				<div className='pn-layout'>
					<aside className='pn-rail'>
						<div className='pn-rail-title'>{t('payload.rail.title', { defaultValue: 'On this page' })}</div>
						<nav className='pn-rail-nav'>
							{SECTIONS
								.filter((s) => (s.id === 'pn-insights' ? hasInsights : true))
								.map((s) => {
									const Icon = Icons[s.icon];
									return (
										<a key={s.id} href={`#${s.id}`} className='pn-rail-link'>
											<span className='pn-rail-icon'>{Icon && <Icon />}</span>
											{t(s.labelKey)}
										</a>
									);
								})
							}
						</nav>

						{isAdminUser && (
							<div className='pn-pdf-link pn-pdf-link--rail'>
								<button type='button' className='pn-pdf-btn' onClick={() => navigate(inspectionHref)}>
									<InspectIcon size={14} />
									<span>{t('surveys.response.inspection.launcher', { defaultValue: 'Inspeccionar' })}</span>
								</button>
							</div>
						)}

						<div className='pn-rail-share'>
							{t('surveys.response.share.prompt')}{' '}
							<button type='button' className='pn-rail-share-btn' onClick={handleCopyUrl}>
								{urlCopied
									? t('surveys.response.share.copied')
									: t('surveys.response.share.copyUrl')}
							</button>
						</div>

						<button type='button' className='ds-tour-launcher' onClick={tour.start}>
							<Icons.compass /> {t('tour.launcher', { defaultValue: 'Take a tour' })}
						</button>
					</aside>

					<div className='pn-main'>
						{locationInfo && <LocationInfoCard sectionId='pn-identity' locationInfo={locationInfo} />}
						<SurveyData sectionId='pn-payload' info={survey_response} schemaLabel={schemaLabel} />
						<PayloadScoreCard
							sectionId='pn-score'
							indicators={scoreIndicators}
							errored={!!indicators_errors?.indicators_results}
						/>

						{hasInsights && (
							<PayloadInsightsCard
								sectionId='pn-insights'
								counts={insightCounts}
								detailsHref={insightsHref}
							/>
						)}

						<section id='pn-content' className='pn-card'>
							<div className='pn-section-header'>
								<h2 className='pn-section-title'>
									<Icons.grid /> {t('surveys.response.content.title')}
								</h2>
							</div>
							<SurveyContent content={parsedFields} />
						</section>

						<div className='pn-mobile-footer'>
							<div className='pn-rail-share'>
								{t('surveys.response.share.prompt')}{' '}
								<button type='button' className='pn-rail-share-btn' onClick={handleCopyUrl}>
									{urlCopied
										? t('surveys.response.share.copied')
										: t('surveys.response.share.copyUrl')}
								</button>
							</div>
							{isAdminUser && (
								<div className='pn-pdf-link pn-pdf-link--hero'>
									<button type='button' className='pn-pdf-btn' onClick={() => navigate(inspectionHref)}>
										<InspectIcon size={14} />
										<span>{t('surveys.response.inspection.launcher', { defaultValue: 'Inspeccionar' })}</span>
									</button>
								</div>
							)}
							<button type='button' className='ds-tour-launcher' onClick={tour.start}>
								<Icons.compass /> {t('tour.launcher', { defaultValue: 'Take a tour' })}
							</button>
						</div>
					</div>
				</div>
			</div>

			<Tour
				steps={tourSteps}
				open={tour.isOpen}
				onClose={tour.stop}
			/>
		</div>
	);
}

export default SurveyResponse;
